import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, User, ArrowRight, ArrowLeft } from "lucide-react";
import { getPostById, getPublishedPosts } from "../data/blogPosts";
import PageLayout from "../components/PageLayout";
import { useSEO } from "../hooks/useSEO";

const BlogPost = () => {
  const { id } = useParams<{ id: string }>();
  const post = id ? getPostById(id) : null;

  // Set SEO meta tags
  useSEO({
    title: post?.title,
    description: post?.excerpt,
    keywords: post?.tags.join(', '),
    author: post?.author,
    canonical: post ? `https://sera.app/blog/${post.id}` : undefined,
    ogTitle: post?.title,
    ogDescription: post?.excerpt,
    ogImage: 'https://sera.app/og-image.png',
    ogUrl: post ? `https://sera.app/blog/${post.id}` : undefined,
    twitterTitle: post?.title,
    twitterDescription: post?.excerpt,
    twitterImage: 'https://sera.app/og-image.png',
    articleAuthor: post?.author,
    articlePublishedTime: post?.date,
    articleModifiedTime: post?.date,
    articleTags: post?.tags
  });

  if (!post) {
    return (
      <PageLayout>
        <div className="pt-24 flex items-center justify-center min-h-[calc(100vh-6rem)]">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
            <p className="text-slate-400 mb-8">
              The blog post you're looking for doesn't exist or hasn't been published yet.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center space-x-2 text-teal-400 hover:text-teal-300 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Blog</span>
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  const relatedPosts = getPublishedPosts()
    .filter(p => p.id !== post.id && p.tags.some(tag => post.tags.includes(tag)))
    .slice(0, 3);

  return (
    <PageLayout>
      {/* Article */}
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12"
      >
        {/* Article Header */}
        <header className="mb-12">
          <div className="flex items-center space-x-4 mb-6">
            <Link
              to="/blog"
              className="flex items-center space-x-2 text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Blog</span>
            </Link>
          </div>
          <div className="flex items-center space-x-4 mb-6 text-sm text-slate-400">
            <div className="flex items-center space-x-1">
              <Calendar className="w-4 h-4" />
              <span>{new Date(post.date).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>{post.readTime}</span>
            </div>
            <div className="flex items-center space-x-1">
              <User className="w-4 h-4" />
              <span>{post.author}</span>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-teal-400 to-blue-500 bg-clip-text text-transparent">
            {post.title}
          </h1>

          <p className="text-xl text-slate-400 mb-8">
            {post.excerpt}
          </p>

          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-slate-800 text-slate-300 text-sm rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        </header>

        {/* Article Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <post.component />
        </motion.div>

        {/* Article Footer */}
        <footer className="mt-12 pt-8 border-t border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-teal-400 to-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <div>
                <p className="font-semibold text-white">{post.author}</p>
                <p className="text-sm text-slate-400">Sera Team</p>
              </div>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center space-x-2 text-teal-400 hover:text-teal-300 transition-colors"
            >
              <span>Back to Blog</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </footer>
      </motion.article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-800/30"
        >
          <div className="max-w-6xl mx-auto">
            <h3 className="text-2xl font-bold mb-8 text-center">Related Posts</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <motion.article
                  key={relatedPost.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="bg-slate-800/50 rounded-xl p-6 hover:bg-slate-800/70 transition-colors group"
                >
                  <div className="flex items-center space-x-3 mb-3 text-xs text-slate-400">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-3 h-3" />
                      <span>{new Date(relatedPost.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{relatedPost.readTime}</span>
                    </div>
                  </div>
                  
                  <h4 className="text-lg font-bold mb-2 group-hover:text-teal-400 transition-colors line-clamp-2">
                    {relatedPost.title}
                  </h4>
                  
                  <p className="text-slate-300 mb-4 text-sm line-clamp-2">
                    {relatedPost.excerpt}
                  </p>
                  
                  <Link
                    to={`/blog/${relatedPost.id}`}
                    className="inline-flex items-center space-x-2 text-teal-400 hover:text-teal-300 transition-colors text-sm group"
                  >
                    <span>Read more</span>
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </motion.section>
      )}
    </PageLayout>
  );
};

export default BlogPost;
